// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React from 'react';
import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';

const PrivacyPage = () => (
  <div className="min-h-screen bg-[#1C1C1A] py-16 px-4" data-testid="privacy-page">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-[820px] mx-auto"
    >
      <div className="flex items-center gap-3 mb-3">
        <Shield className="w-6 h-6 text-[#C9A84C]" />
        <h1 className="text-4xl font-bold text-[#E8E8E4]">Privacy Policy</h1>
      </div>
      <p className="text-sm text-[#888780] mb-10">Ultimo aggiornamento: Maggio 2026 — Versione 2.1</p>

      <div className="space-y-8 text-[#888780] leading-relaxed text-base">

        {/* TITOLARE — required at top */}
        <section data-testid="privacy-titolare">
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">1. Titolare del trattamento</h2>
          <div className="p-4 rounded-xl space-y-1" style={{ background: '#252523', border: '1px solid rgba(201,168,76,0.25)' }}>
            <p><strong className="text-[#E8E8E4]">Titolare:</strong> Gian Marco Cappelli</p>
            <p><strong className="text-[#E8E8E4]">Sede:</strong> Torino, Italia</p>
            <p><strong className="text-[#E8E8E4]">Contatto privacy:</strong> <a href="mailto:privacy@finsapiens.it" className="text-[#C9A84C]">privacy@finsapiens.it</a></p>
            <p><strong className="text-[#E8E8E4]">Sito:</strong> <a href="https://finsapiens.it" className="text-[#C9A84C]">finsapiens.it</a></p>
          </div>
          <p className="mt-3 text-sm">Il presente documento descrive come SAPIENS tratta i dati personali ai sensi del <strong className="text-[#E8E8E4]">Regolamento UE 2016/679 (GDPR)</strong>, del <strong className="text-[#E8E8E4]">D.Lgs. 196/2003</strong> (Codice Privacy) e successive modifiche, e del <strong className="text-[#E8E8E4]">D.Lgs. 101/2018</strong>.</p>
        </section>

        {/* DATI RACCOLTI + BASI GIURIDICHE */}
        <section>
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">2. Dati raccolti, finalità e basi giuridiche</h2>
          <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#252523' }}>
                  <th className="text-left p-3 text-[#E8E8E4] font-semibold">Categoria di dati</th>
                  <th className="text-left p-3 text-[#E8E8E4] font-semibold">Finalità</th>
                  <th className="text-left p-3 text-[#E8E8E4] font-semibold">Base giuridica</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-white/[0.06]">
                  <td className="p-3 text-[#888780]">Email, nome, foto profilo (Google OAuth)</td>
                  <td className="p-3 text-[#888780]">Account utente, salvataggio progresso, notifiche</td>
                  <td className="p-3 text-[#888780]">Esecuzione contratto — art. 6.1.b GDPR</td>
                </tr>
                <tr className="border-t border-white/[0.06]">
                  <td className="p-3 text-[#888780]">Dati di pagamento (Stripe)</td>
                  <td className="p-3 text-[#888780]">Sottoscrizione Premium</td>
                  <td className="p-3 text-[#888780]">Esecuzione contratto — art. 6.1.b GDPR</td>
                </tr>
                <tr className="border-t border-white/[0.06]">
                  <td className="p-3 text-[#888780]">Email newsletter</td>
                  <td className="p-3 text-[#888780]">Invio comunicazioni didattiche</td>
                  <td className="p-3 text-[#888780]">Consenso — art. 6.1.a GDPR</td>
                </tr>
                <tr className="border-t border-white/[0.06]">
                  <td className="p-3 text-[#888780]">Cookie analitici (GA4 anonymized)</td>
                  <td className="p-3 text-[#888780]">Statistiche aggregate uso del sito</td>
                  <td className="p-3 text-[#888780]">Consenso — art. 6.1.a GDPR + art. 122 Cod. Privacy</td>
                </tr>
                <tr className="border-t border-white/[0.06]">
                  <td className="p-3 text-[#888780]">Log tecnici (IP troncato, user-agent)</td>
                  <td className="p-3 text-[#888780]">Sicurezza, prevenzione abusi</td>
                  <td className="p-3 text-[#888780]">Legittimo interesse — art. 6.1.f GDPR</td>
                </tr>
                <tr className="border-t border-white/[0.06]">
                  <td className="p-3 text-[#888780]">Codice referral utilizzato</td>
                  <td className="p-3 text-[#888780]">Programma referral (30gg Premium)</td>
                  <td className="p-3 text-[#888780]">Esecuzione contratto — art. 6.1.b GDPR</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-sm">I calcolatori finanziari di SAPIENS — incluso il <strong className="text-[#E8E8E4]">Lettore Busta Paga</strong> — sono <strong className="text-[#3D7A52]">interamente client-side</strong>: i dati inseriti rimangono sul tuo dispositivo e non vengono mai trasmessi al server SAPIENS.</p>
        </section>

        {/* COOKIE */}
        <section data-testid="privacy-cookie-section">
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">3. Cookie</h2>
          <p>SAPIENS utilizza cookie tecnici (necessari) e, previo consenso esplicito, cookie analitici Google Analytics 4. Per il dettaglio completo dei cookie utilizzati, durata, finalità e modalità di gestione consulta la <a href="/cookie-policy" className="text-[#C9A84C] underline">Cookie Policy</a>.</p>
          <p className="mt-2">Puoi <strong className="text-[#E8E8E4]">revocare il consenso</strong> in qualsiasi momento cliccando sull'icona 🍪 in basso a sinistra del sito.</p>
        </section>

        {/* TRASFERIMENTI EXTRA-UE */}
        <section data-testid="privacy-extra-eu">
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">4. Trasferimenti di dati al di fuori dell'UE</h2>
          <p>Alcuni dei nostri fornitori tecnici hanno sede o server al di fuori dell'Unione Europea (USA in particolare). Per questi trasferimenti applichiamo le tutele previste dal <strong className="text-[#E8E8E4]">Capo V del GDPR</strong>:</p>
          <ul className="mt-3 space-y-2 list-disc list-inside text-sm">
            <li><strong className="text-[#E8E8E4]">Google (Google OAuth, Google Analytics 4)</strong>: dati trasferiti negli USA in base al <em>EU-U.S. Data Privacy Framework</em> (Decisione di adeguatezza Commissione UE del 10/07/2023) e a Clausole Contrattuali Standard (SCC).</li>
            <li><strong className="text-[#E8E8E4]">Stripe (pagamenti)</strong>: dati trasferiti negli USA con SCC e cifratura PCI-DSS.</li>
          </ul>
          <p className="mt-3 text-sm">Puoi richiederci copia delle SCC scrivendo a <a href="mailto:privacy@finsapiens.it" className="text-[#C9A84C]">privacy@finsapiens.it</a>.</p>
        </section>

        {/* CONSERVAZIONE */}
        <section>
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">5. Periodo di conservazione</h2>
          <ul className="space-y-2 list-disc list-inside text-sm">
            <li>Dati account: per tutta la durata dell'account + 12 mesi dopo cancellazione.</li>
            <li>Dati di pagamento: 10 anni (obbligo fiscale, art. 2220 c.c.).</li>
            <li>Email newsletter: fino alla revoca del consenso.</li>
            <li>Cookie analitici: max 26 mesi.</li>
            <li>Log tecnici: 12 mesi.</li>
          </ul>
        </section>

        {/* DIRITTI DEGLI INTERESSATI */}
        <section>
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">6. I tuoi diritti (artt. 15-22 GDPR)</h2>
          <ul className="space-y-2 list-disc list-inside text-sm">
            <li><strong className="text-[#E8E8E4]">Accesso</strong> ai tuoi dati personali</li>
            <li><strong className="text-[#E8E8E4]">Rettifica</strong> di dati inesatti</li>
            <li><strong className="text-[#E8E8E4]">Cancellazione</strong> (diritto all'oblio)</li>
            <li><strong className="text-[#E8E8E4]">Limitazione</strong> del trattamento</li>
            <li><strong className="text-[#E8E8E4]">Portabilità</strong> in formato leggibile (JSON/CSV)</li>
            <li><strong className="text-[#E8E8E4]">Opposizione</strong> al trattamento</li>
            <li><strong className="text-[#E8E8E4]">Revoca del consenso</strong> in qualsiasi momento</li>
            <li><strong className="text-[#E8E8E4]">Reclamo al Garante Privacy</strong>: <a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C]">garanteprivacy.it</a></li>
          </ul>
          <p className="mt-3 text-sm">Per esercitare i tuoi diritti scrivi a <a href="mailto:privacy@finsapiens.it" className="text-[#C9A84C]">privacy@finsapiens.it</a>. Risponderemo entro 30 giorni (art. 12.3 GDPR).</p>
        </section>

        {/* DATA BREACH */}
        <section data-testid="privacy-data-breach">
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">7. Violazioni di dati (Data Breach)</h2>
          <p>In caso di violazione dei dati personali che possa comportare un rischio elevato per i diritti e le libertà degli interessati, SAPIENS notificherà l'evento al <strong className="text-[#E8E8E4]">Garante Privacy entro 72 ore</strong> dalla scoperta (art. 33 GDPR) e ne darà comunicazione agli utenti coinvolti senza ingiustificato ritardo (art. 34 GDPR).</p>
          <p className="mt-2 text-sm">Manteniamo un registro interno delle violazioni e applichiamo misure di sicurezza tecniche e organizzative adeguate (art. 32 GDPR): cifratura TLS, hash bcrypt delle password, accesso minimizzato, log accessi, backup giornalieri.</p>
        </section>

        {/* MINORI */}
        <section>
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">8. Minori di 18 anni</h2>
          <p>SAPIENS è destinato a utenti maggiorenni (≥18 anni). Non raccogliamo consapevolmente dati di minori. Se sei genitore o tutore e ritieni che il tuo minore abbia fornito dati al servizio, contattaci per la cancellazione immediata.</p>
        </section>

        {/* MODIFICHE */}
        <section>
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">9. Modifiche alla presente Privacy Policy</h2>
          <p>Eventuali modifiche saranno pubblicate su questa pagina con aggiornamento della data in alto. In caso di modifiche sostanziali, sarai informato tramite email (per gli utenti registrati) o tramite banner sul sito.</p>
        </section>

        {/* CONTATTI */}
        <section>
          <h2 className="text-xl font-semibold text-[#E8E8E4] mb-3">10. Contatti</h2>
          <p>Per qualsiasi domanda relativa al trattamento dei dati: <a href="mailto:privacy@finsapiens.it" className="text-[#C9A84C]">privacy@finsapiens.it</a></p>
        </section>

      </div>
    </motion.div>
  </div>
);

export default PrivacyPage;
